import React from 'react';
import styled, { useTheme } from 'styled-components';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import EmptyBookmarkIcon from 'app/ui/components/atoms/icons/bookmark/empty';
import FilledBookmarkIcon from 'app/ui/components/atoms/icons/bookmark/filled';
import TickIcon from 'app/ui/components/atoms/icons/tick';
import Button from 'app/ui/components/atoms/button';
import useConfig from 'app/config';
import { appendSessionId } from 'app/utils/analytics/recordrelay/session';
import { useAppState } from 'state';

const Actions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  margin-bottom: 2rem;
`;

const StyledEmptyBookmarkIcon = styled(EmptyBookmarkIcon)`
  height: 1rem;
`;

const StyledFilledBookmarkIcon = styled(FilledBookmarkIcon)`
  height: 1rem;
`;

const EditButtonContainer = styled.div`
  position: relative;
`;

type Props = {
  pdfExportEnabled: boolean;
  isSaved: boolean;
  isCompleted: boolean;
  isMigrated: boolean;
  saveAction: () => void;
  unSaveAction: () => void;
  completeAction: () => void;
  unCompleteAction: () => void;
  onPdfExport?: () => void;
  className?: string;
  canAccessWorkoutBuilder: boolean;
  workoutConcept?: string;
};

const WorkoutActions = ({
  pdfExportEnabled,
  isSaved,
  isCompleted,
  saveAction,
  unSaveAction,
  completeAction,
  unCompleteAction,
  onPdfExport,
  className,
  canAccessWorkoutBuilder,
  workoutConcept,
}: Props) => {
  const theme = useTheme();
  const intl = useIntl();
  const { config } = useConfig();
  const { id: lessonId } = useParams<{ id: string }>();
  const consentType = useAppState(({ cookieConsent }) => cookieConsent.consentType);

  const saveLabel = intl.formatMessage({ id: 'lesson.buttons.save' });
  const savedLabel = intl.formatMessage({ id: 'lesson.buttons.saved' });
  const completeLabel = intl.formatMessage({ id: 'lesson.buttons.complete' });
  const completedLabel = intl.formatMessage({ id: 'lesson.buttons.completed' });
  const editLabel = intl.formatMessage({ id: 'lesson.buttons.edit' });
  const generatePdfLabel = intl.formatMessage({ id: 'lesson.buttons.generatePdf' });
  const showEditButton =
    workoutConcept === 'Power' ||
    workoutConcept === 'Engine' ||
    workoutConcept === 'Complete' ||
    workoutConcept === 'Foundational';
  return (
    <Actions className={className}>
      {isSaved ? (
        <Button
          onClick={unSaveAction}
          label={savedLabel}
          iconPosition="left"
          icon={<StyledFilledBookmarkIcon color={theme.colors.white} />}
          type="curved"
        />
      ) : (
        <Button
          onClick={saveAction}
          label={saveLabel}
          iconPosition="left"
          icon={<StyledEmptyBookmarkIcon color={theme.colors.white} />}
          type="curved-grey"
        />
      )}
      {isCompleted ? (
        <Button
          onClick={unCompleteAction}
          label={completedLabel}
          iconPosition="left"
          icon={<TickIcon color={theme.colors.white} />}
          type="curved"
        />
      ) : (
        <Button onClick={completeAction} label={completeLabel} type="curved-grey" />
      )}
      {onPdfExport && pdfExportEnabled && <Button onClick={onPdfExport} label={generatePdfLabel} type="curved-grey" />}
      {showEditButton && canAccessWorkoutBuilder && (
        <EditButtonContainer>
          <Button
            onClick={() => {
              const subpath = `workout-builder/import/${lessonId}`;
              const baseUrl = `${config.REACT_APP_WORKOUT_BUILDER_URL}?client_id=${config.REACT_APP_CLIENT_ID_FOR_ONE_FIIT}&subpath=${encodeURIComponent(subpath)}`;
              window.location.href = consentType === 'all' ? appendSessionId(baseUrl) : baseUrl;
            }}
            label={editLabel}
            type="curved-grey"
          />
        </EditButtonContainer>
      )}
    </Actions>
  );
};

export default WorkoutActions;
